import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { TransactionTypesOptions, Card, Box, withStyles, formatAmount, TransactionStatusStepper } from 'vopay';
import { Title } from 'UI';
import { useToast } from '@/providers/Toast.provider';
import useAccountBearer from '@/components/Entities/hooks/useApi/useAccountBearer';
import useFetchTransactionHistory from '@/hooks/useApi/useFetchTransactionHistory';
import styles from './TransactionCard.module.scss';

type TransactionCardProps = {
  receiverName: string;
  amount: string;
  currency: string;
  transactionId: string;
  type: TransactionTypesOptions;
  /** The account the transaction belongs to; scopes the status history this card fetches. */
  accountId?: string;
  failureReason?: string;
};

const cn = withStyles(styles);

export default function TransactionCard({
  receiverName,
  amount,
  currency,
  transactionId,
  type,
  accountId,
  failureReason = '',
}: TransactionCardProps) {
  const { t } = useTranslation('translations', {
    keyPrefix: 'Transaction.Details.TransactionCard',
  });

  const isWithdrawal = type.toLowerCase().includes('withdrawal');
  const isFunding = type.toLowerCase().includes('funding');
  const toast = useToast();
  const { bearer } = useAccountBearer(accountId ?? '');
  const { transactionHistory, fetchTransactionHistory } = useFetchTransactionHistory({ bearer });

  useEffect(() => {
    if (!transactionId || !bearer) return;

    (async () => {
      try {
        await fetchTransactionHistory({
          TransactionID: transactionId,
        });
      } catch (err) {
        console.error(err);
        toast.neutral(t('historyFetchingError', { defaultValue: 'Failed to fetch transaction history' }));
      }
    })();
  }, [transactionId, bearer]);

  return (
    <Card className={cn('card')}>
      <Box jc="sb" gapSize="24px">
        <Box flx="col" gapSize="24px">
          {/* Plain text: this card is only ever rendered on the account's own transaction page, so the
              name used to link to the account the reader is already inside. */}
          <Title level="2" data-testid="transactionCardReceiver">
            {receiverName}
          </Title>

          <Box flx="col" gapSize="32px" padding="0 0 0 6px">
            <Box flx="col" gapSize="5px">
              <Title level="5" emphasized={false} className={cn('grey')}>
                {isWithdrawal
                  ? t('amountSent', { defaultValue: 'Amount Sent' })
                  : isFunding
                    ? t('amountRequested', { defaultValue: 'Amount Requested' })
                    : t('amount', { defaultValue: 'Amount' })}
              </Title>

              <Title level="4" data-testid="transactionCardAmount">
                {currency} {formatAmount(amount)}
              </Title>
            </Box>

            <div className={cn('idContainer')}>
              <Box flx="col" gapSize="6px">
                <div className={cn('grey', 'row')}>{t('transactionId', { defaultValue: 'Transaction ID' })}</div>
                <div className={cn('fontWeight500')}>{transactionId}</div>
              </Box>
            </div>
          </Box>
        </Box>

        <TransactionStatusStepper transactionHistory={transactionHistory} failureReason={failureReason} />
      </Box>
    </Card>
  );
}
